'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { API_BASE_URL } from 'lib/config/apiEndpoints';
import { emailRegex } from 'utils/data';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { CaretUp, InfoIcon } from 'assets/icons';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import {
  ConsentPlugType,
  getPurchaseCodeForApi,
  getConsentPlugType,
  isValidPowerPlugCodeFormat,
  requiresConsent,
} from 'content/powerPlugRedeem';
import { ConsentModal } from './ConsentModal';
import styles from './RedeemForm.module.css';

interface Country {
  id: number;
  name: string;
  iso2: string;
  country_code: string;
  phone_code: string;
}

interface RedeemApiResponse {
  success: boolean;
  message: string;
  powerplug_name?: string;
}

export const RedeemForm = ({ className }: { className?: string }) => {
  useGlobalUI({ liteUI: true });
  const t = useTranslations('powerPlugsRedeem');

  const [screen, setScreen] = useState<'form' | 'success'>('form');
  const [countries, setCountries] = useState<Country[]>([]);
  const [redeemMessage, setRedeemMessage] = useState('');
  const [purchaseCode, setPurchaseCode] = useState('');
  const [email, setEmail] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [country, setCountry] = useState<Country>();
  const [isLoading, setIsLoading] = useState(false);
  const [successPowerplugName, setSuccessPowerplugName] = useState('');
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentPlugType, setConsentPlugType] = useState<ConsentPlugType | null>(
    null,
  );

  const location = useRegionLocale();

  const submitEnabled =
    purchaseCode &&
    isValidPowerPlugCodeFormat(purchaseCode) &&
    email &&
    email.match(emailRegex) &&
    emailConfirm &&
    email === emailConfirm &&
    country &&
    !isLoading;

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch('/api/geo/countries.json');
        const data = await response.json();
        setCountries(data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const selectedCountry = countries.find(
      (c) => c.iso2.toLowerCase() === location.region?.toLowerCase(),
    );

    if (selectedCountry) {
      setCountry(selectedCountry);
    }
  }, [location, countries]);

  const redeemCode = useCallback(async () => {
    setRedeemMessage('');
    setIsLoading(true);

    const bodyData = {
      purchase_code: getPurchaseCodeForApi(purchaseCode),
      email,
      region: country?.iso2,
    };

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/web_v1/powerplug_purchase_code`,
        {
          method: 'POST',
          body: JSON.stringify(bodyData),
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      const data: RedeemApiResponse = await response.json();
      console.log('data', data);

      if (data.success) {
        setSuccessPowerplugName(data.powerplug_name || '');
        setScreen('success');
      } else {
        setRedeemMessage(data.message);
      }
    } catch (error) {
      console.error('Error redeeming powerplug code:', error);
      setRedeemMessage(t('form.errors.generic'));
    } finally {
      setIsLoading(false);
      setConsentOpen(false);
    }
  }, [purchaseCode, email, country, t]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (requiresConsent(purchaseCode)) {
      const plugType = getConsentPlugType(purchaseCode);
      if (plugType) {
        setConsentPlugType(plugType);
        setConsentOpen(true);
        return;
      }
    }

    redeemCode();
  };

  const handleConsentConfirm = () => {
    redeemCode();
  };

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCountry = countries.find(
      (c) => c.name === event.target.value,
    );

    if (selectedCountry) {
      setCountry(selectedCountry);
    }
  };

  if (screen === 'success') {
    return (
      <div className={`${styles.root} ${styles.isSuccess}`}>
        <div className={styles.successBlock}>
          <h2>
            {t('success.title', {
              powerplugName: successPowerplugName || 'PowerPlug',
            })}
          </h2>
          <p>{t('success.description')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      <form onSubmit={handleSubmit}>
        <div className={styles.form}>
          <div className={styles.textDescription}>
            <h1>{t('form.title')}</h1>
            <p>{t('form.description')}</p>
          </div>
          <div className={`contact-details ${styles.block}`}>
            <div className={styles.inputRows}>
              <label>
                <span className={styles.lableTitle}>{t('form.purchaseCode')}</span>
                <input
                  type="text"
                  name="purchaseCode"
                  placeholder={t('form.purchaseCodePlaceholder')}
                  required
                  value={purchaseCode}
                  onChange={(e) => setPurchaseCode(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>
              <label>
                <span className={styles.lableTitle}>{t('form.email')}</span>
                <input
                  type="email"
                  name="email"
                  placeholder={t('form.emailPlaceholder')}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </label>
              <label>
                <span className={styles.lableTitle}>{t('form.confirmEmail')}</span>
                <input
                  type="email"
                  name="email-confirm"
                  placeholder={t('form.emailPlaceholder')}
                  required
                  value={emailConfirm}
                  onChange={(e) => setEmailConfirm(e.target.value)}
                  autoComplete="email"
                />
              </label>
              <label>
                <span className={styles.lableTitle}>{t('form.country')}</span>
                <div className={styles.selectWrapper}>
                  <select
                    id="country"
                    value={
                      country?.name ||
                      (location?.countryName ? location.countryName : '')
                    }
                    onChange={handleCountryChange}
                    required
                  >
                    <option value="">{t('form.selectCountry')}</option>
                    {countries.map((c) => (
                      <option key={c.iso2} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <CaretUp
                    style={{
                      transform: 'rotate(180deg) translateY(50%)',
                      position: 'absolute',
                      top: '50%',
                      right: '12px',
                      pointerEvents: 'none',
                    }}
                  />
                </div>
              </label>
            </div>
          </div>
          <div className={styles.infoErrorContainer}>
            {redeemMessage ? (
              <p>
                <InfoIcon
                  fill="rgba(255,255,255,0.5)"
                  style={{ marginRight: '4px' }}
                />
                {redeemMessage}
              </p>
            ) : null}
          </div>
          <button
            className={styles.submit}
            type="submit"
            disabled={!submitEnabled}
          >
            {isLoading ? t('form.submitting') : t('form.submit')}
          </button>
        </div>
      </form>

      {consentPlugType ? (
        <ConsentModal
          plugType={consentPlugType}
          isOpen={consentOpen}
          isLoading={isLoading}
          onClose={() => setConsentOpen(false)}
          onConfirm={handleConsentConfirm}
        />
      ) : null}
    </div>
  );
};
